import React, { useState, useEffect, useRef, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import _, { isEmpty } from 'lodash';
// import Pagination from 'modules/common/components/Pagination';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Acl } from 'modules/common/components/Acl';
import { getJson } from 'session';
import emptyStateImage from 'assets/img/icn-empty-state.svg';
import DOMPurify from 'dompurify';
import ContentEditable from 'react-contenteditable';
import Select from 'react-select';
import { civilStatusOption } from 'modules/civil-registry/container/birth/container/BirthContainer';
import { cedulaStatusOption } from 'modules/CDL/individual/container/IndividualContainer';
import { bplsStatusOption } from 'modules/BPLS/container/applications/container/Applications';
import * as actions from '../action';
import * as c from '../constant';

const MODULE_OPTIONS = [
  { label: 'Local Civil Registry (LCR)', value: 'LCR' },
  { label: 'Business Permit and License Application (BPLS)', value: 'BPLS' },
  { label: 'Community Tax Certificate CTC / CEDULA', value: 'CDL' },
];

const sanitizeConf = {
  ALLOWED_TAGS: ['h3', 'span', 'p'],
  ALLOWED_ATTR: ['class'],
};

const INIT_FORM_DATA = {
  guard_name: '',
  name: '',
  page: '',
  permissions: [],
  notification_events: [],
  notification_events_modules: [],
};

function RoleManagementContent({
  handleAddModal,
  handleDuplicateModal,
  handleRemove,
  selectedRole,
  showRole,
  roleInfo,
  updateRole,
  getPermissionList,
  userPermissions,
  getPermissionGroup,
  permissionGroup,
  getRoleList,
  permissionList,
  // handleEdit,
  // handleAddPermission,
  // handleRemovePermission,
  // permissionListPager,
  isLoading,
  roleParams,
}) {
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [isEditing, setIsEditing] = useState(false);
  const nameRef = useRef();

  const isFormDirty = useMemo(() => {
    if (
      _.isEqual(
        permissionList?.map((p) => p.attributes.name).sort(),
        formData.permissions.sort()
      ) &&
      _.get(roleInfo, 'attributes.name') === formData.name &&
      _.get(roleInfo, 'attributes.page') === formData.page &&
      _.isEqual(
        (_.get(roleInfo, 'attributes.notification_events', []) || []).sort(),
        formData.notification_events.sort()
      )
    )
      return false;

    return true;
  }, [permissionList, roleInfo, formData]);

  // const handleChangePage = (page) => {
  //   setFilterPageData({
  //     ...pageFilterData,
  //     page,
  //   });
  // };

  useEffect(() => {
    if (_.isEmpty(selectedRole)) return;
    setIsEditing(false);
    showRole(selectedRole);
    getPermissionList(selectedRole);
  }, [selectedRole]);

  useEffect(() => {
    getPermissionGroup();
  }, []);

  const setDefaultForm = () => {
    setFormData((prev) => ({
      ...prev,
      guard_name: _.get(roleInfo, 'attributes.guard_name', ''),
      name: _.get(roleInfo, 'attributes.name', ''),
      page: _.get(roleInfo, 'attributes.page', ''),
      permissions: permissionList?.map((p) => p.attributes.name) || [],
      notification_events_modules:
        _.get(roleInfo, 'attributes.notification_events_modules') || [],
      notification_events:
        _.get(roleInfo, 'attributes.notification_events') || [],
    }));
  };

  useEffect(() => {
    setDefaultForm();
  }, [JSON.stringify(roleInfo), JSON.stringify(permissionList)]);

  const handleChange = React.useCallback((evt, name) => {
    const value = DOMPurify.sanitize(evt.target.value, sanitizeConf);
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleToggleSwitch = () => {
    nameRef.current.focus();
    if (isEditing) {
      setDefaultForm();
    }
    setIsEditing(!isEditing);
  };

  const focusContentEditable = () => {
    if (nameRef.current) {
      const el = nameRef.current;
      el.focus();

      // Move cursor to the end
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false); // false means collapse to end
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  useEffect(() => {
    if (isEditing) focusContentEditable();
  }, [isEditing]);

  const handleSelectChange = (e, key) => {
    if (!e) {
      setFormData((prev) => ({
        ...prev,
        [key]: [],
      }));
      return;
    }

    if (key === 'notification_events_modules') {
      setFormData((prev) => ({
        ...prev,
        [key]: e.map((x) => x.value),
        notification_events: [],
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [key]: e ? e.map((x) => x.label) : [],
    }));
  };

  const handleSave = () => {
    updateRole(selectedRole, formData, () => {
      getRoleList(roleParams);
      getPermissionList(selectedRole);
      showRole(selectedRole);
      setIsEditing(false);
    });
  };

  const handleCancel = () => {
    setDefaultForm();
    setIsEditing(false);
  };

  const handleSelectAll = (e) => {
    const { checked } = e.target;
    if (checked) {
      const permissionArray = permissionGroup
        .map((g) => g.attributes.permissions.map((p) => p.name))
        .flat();
      setFormData((prev) => ({
        ...prev,
        permissions: permissionArray,
      }));
      return;
    }
    setDefaultForm();
  };

  const handleSelectAllByGroup = (e, groupArray) => {
    const { checked } = e.target;
    if (checked) {
      setFormData((prev) => ({
        ...prev,
        permissions: [...prev.permissions, ...groupArray],
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.filter((p) => !groupArray.includes(p)),
    }));
  };

  const isAllIncluded = () => {
    const permissionArray = permissionGroup
      .map((g) => g.attributes.permissions.map((p) => p.name))
      .flat();
    return _.isEqual(formData.permissions.sort(), permissionArray.sort());
  };

  const isGroupIncluded = (groupArray) => {
    return groupArray.every((g) => formData.permissions.includes(g));
  };

  const STATUS_OPTIONS = useMemo(() => {
    if (isEmpty(formData.notification_events_modules)) return [];
    let allOptions = [];
    if (formData.notification_events_modules.includes('LCR')) {
      allOptions = [...allOptions, ...civilStatusOption];
    }

    if (formData.notification_events_modules.includes('CDL')) {
      allOptions = [...allOptions, ...cedulaStatusOption];
    }

    if (formData.notification_events_modules.includes('BPLS')) {
      allOptions = [...allOptions, ...bplsStatusOption];
    }

    // Remove duplicates if any options have the same value
    const uniqueOptions = [
      ...new Set(
        allOptions
          .filter(
            (option) =>
              option.value !== '' &&
              option.value !== undefined &&
              option.value !== null
          )
          .map((option) => ({ label: option.label, value: option.label }))
      ),
    ];

    return uniqueOptions;
  }, [formData.notification_events_modules]);

  const getNotificationEventValues = () => {
    return formData.notification_events.map((val) => ({
      label: val,
      value: val,
    }));
  };

  return (
    <div
      className="d-flex flex-column p-3 position-relative"
      style={{ overflowY: 'hidden' }}
    >
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 'bold',
          }}
        >
          Loading...
        </div>
      )}

      {Acl(userPermissions, ['VIEW ROLE MANAGEMENT']) && (
        <div className="d-flex align-items-center justify-content-end mb-3">
          <button
            type="button"
            onClick={handleAddModal}
            className="btn mr-auto btn-sm btn-primary"
          >
            <i className="fas fa-plus" /> Add
          </button>

          {!_.isEmpty(roleInfo) && (
            <>
              <button
                type="button"
                className="btn mr-3 btn-sm btn-secondary"
                onClick={handleDuplicateModal(roleInfo)}
              >
                <i className="far fa-copy" /> Duplicate
              </button>

              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={handleRemove(roleInfo)}
              >
                <i className="far fa-trash-alt" /> Delete
              </button>
            </>
          )}
        </div>
      )}

      {!_.isEmpty(roleInfo) ? (
        <>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <label className="d-flex flex-column flex-grow-1 pr-3">
              <small>Role Name:</small>
              <ContentEditable
                innerRef={nameRef}
                className="font-weight-bold mb-0 hover:bg-black p-1 content-editable"
                disabled={!isEditing}
                tagName="h3"
                html={formData.name}
                onChange={(e) => handleChange(e, 'name')}
                style={{
                  outline: 'none',
                  borderRadius: '2px',
                  cursor: isEditing ? 'text' : 'default',
                }}
              />
            </label>

            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="customSwitch1"
                checked={isEditing}
                onChange={handleToggleSwitch}
              />
              <label
                className="custom-control-label pt-1"
                htmlFor="customSwitch1"
              >
                Enable Edit
              </label>
            </div>
          </div>

          <label className="mb-2 d-flex flex-column">
            <small>Redirect Page:</small>
            <h4 className="mb-0 p-1">
              <span style={{ opacity: 0.5 }}>{window.location.origin}/</span>
              <ContentEditable
                className="mb-0 content-editable"
                disabled={!isEditing}
                tagName="span"
                html={formData.page || ''}
                onChange={(e) => handleChange(e, 'page')}
                style={{
                  outline: 'none',
                  borderRadius: '2px',
                  cursor: isEditing ? 'text' : 'default',
                  fontWeight: 'bold',
                  opacity: isEditing ? 1 : 0.5,
                }}
              />
            </h4>
          </label>

          <label className="d-flex flex-column">
            <small>Notification Events Module:</small>
            <Select
              value={formData.notification_events_modules.map((val) => ({
                label: val,
                value: val,
              }))}
              isMulti
              closeMenuOnSelect={false}
              name="notification_events_modules"
              options={MODULE_OPTIONS}
              className="basic-multi-select"
              classNamePrefix="select"
              isDisabled={!isEditing}
              placeholder="Module..."
              onChange={(e) =>
                handleSelectChange(e, 'notification_events_modules')
              }
            />
          </label>

          <label className="mb-3 d-flex flex-column">
            <small>Notification Events:</small>
            <Select
              value={getNotificationEventValues()}
              isMulti
              closeMenuOnSelect={false}
              name="notification_events"
              options={STATUS_OPTIONS}
              className="basic-multi-select mb-3"
              classNamePrefix="select"
              isDisabled={
                !isEditing || isEmpty(formData.notification_events_modules)
              }
              placeholder="Notification Events..."
              onChange={(e) => handleSelectChange(e, 'notification_events')}
            />
          </label>

          <div className="mb-1 d-flex justify-content-between align-items-center">
            <h4
              className="font-weight-bold mb-0"
              style={{ fontSize: '.8rem', textTransform: 'uppercase' }}
            >
              Permissions
            </h4>

            <div className="form-group form-check mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id="selectAll"
                disabled={!isEditing}
                checked={isAllIncluded()}
                onChange={handleSelectAll}
              />
              <label
                className="form-check-label"
                htmlFor="selectAll"
                style={{ fontSize: '.8rem', paddingTop: '2px' }}
              >
                Select All
              </label>
            </div>
          </div>

          <div
            className="position-relative mb-3"
            style={{ height: '45.5svh', overflowY: 'auto' }}
          >
            <div
              style={{
                position: 'absolute',
                width: '100%',
                top: 0,
                left: 0,
              }}
            >
              <div className="table-responsive">
                <table className="table table-bordered transaction-table">
                  <tbody>
                    {permissionGroup
                      ? permissionGroup.map((permission) => {
                          const permissionItem = _.get(
                            permission,
                            'attributes.permissions'
                          );
                          return (
                            <tr key={`permission-group-${permission.id}`}>
                              <td className="text-dark" width="250">
                                <div className="form-group form-check m-3">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="selectAllByGroup"
                                    disabled={!isEditing}
                                    checked={isGroupIncluded(
                                      permissionItem.map((i) => i.name)
                                    )}
                                    onChange={(e) =>
                                      handleSelectAllByGroup(
                                        e,
                                        permissionItem.map((i) => i.name)
                                      )
                                    }
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="selectAllByGroup"
                                    style={{
                                      fontSize: '.8rem',
                                      paddingTop: '2px',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {_.get(permission, 'attributes.name')}
                                  </label>
                                </div>
                              </td>

                              <td>
                                <div
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '10px',
                                  }}
                                  className="m-3"
                                >
                                  {permissionItem
                                    ? permissionItem.map((item) => {
                                        const isIncluded =
                                          formData.permissions.includes(
                                            item.name
                                          );

                                        return (
                                          <div
                                            className="form-group form-check mb-0"
                                            key={item.id}
                                          >
                                            <input
                                              type="checkbox"
                                              className="form-check-input"
                                              id="selectPermission"
                                              disabled={!isEditing}
                                              checked={isIncluded}
                                              onChange={() => {
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  permissions: isIncluded
                                                    ? formData.permissions.filter(
                                                        (x) => x !== item.name
                                                      )
                                                    : [
                                                        ...prev.permissions,
                                                        item.name,
                                                      ],
                                                }));
                                              }}
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="selectPermission"
                                              style={{
                                                fontSize: '.7rem',
                                                paddingTop: '3px',
                                              }}
                                            >
                                              {_.get(item, 'name')}
                                            </label>
                                          </div>
                                        );
                                      })
                                    : []}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-end">
            <button
              disabled={!isEditing}
              type="button"
              className="btn btn-sm btn-light mr-3"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              disabled={!isEditing || !isFormDirty}
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </>
      ) : (
        <div className="d-flex align-items-center justify-content-center h-100">
          <div className="text-center mx-auto py-4">
            <img
              src={emptyStateImage}
              className="img-responsive image-wrapper mb-4 mt-3"
              alt="Empty State"
            />
            <p className="emp-title mb-0"> No Item Found </p>
          </div>
        </div>
      )}

      {/* {permissionList && permissionList.length < 0 && (
        <Pagination onChange={handleChangePage} data={permissionListPager} />
      )} */}
    </div>
  );
}

RoleManagementContent.propTypes = {
  handleAddModal: PropTypes.instanceOf(Function).isRequired,
  handleDuplicateModal: PropTypes.instanceOf(Function).isRequired,
  handleRemove: PropTypes.instanceOf(Function).isRequired,
  showRole: PropTypes.instanceOf(Function).isRequired,
  updateRole: PropTypes.instanceOf(Function).isRequired,
  getRoleList: PropTypes.instanceOf(Function).isRequired,
  roleInfo: PropTypes.instanceOf(Object).isRequired,
  selectedRole: PropTypes.string.isRequired,
  getPermissionList: PropTypes.instanceOf(Function).isRequired,
  userPermissions: PropTypes.instanceOf(Object).isRequired,
  getPermissionGroup: PropTypes.instanceOf(Function).isRequired,
  permissionGroup: PropTypes.instanceOf(Array).isRequired,
  permissionList: PropTypes.instanceOf(Array).isRequired,
  // handleEdit: PropTypes.instanceOf(Function).isRequired,
  // handleAddPermission: PropTypes.instanceOf(Function).isRequired,
  // handleRemovePermission: PropTypes.instanceOf(Function).isRequired,
  // permissionListPager: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  roleParams: PropTypes.instanceOf(Object).isRequired,
};

const mapStateToProps = ({ api }, { match }) => ({
  roleInfo: _.get(api, `${c.GET_SELECTED_ROLE}.item`) || {},
  selectedRole: _.get(match, 'params.roleId') || '',
  userPermissions: getJson('permissions'),
  permissionGroup: _.get(api, `${c.GET_PERMISSIONS_GROUP}.list`) || [],
  permissionList: _.get(api, `${c.GET_PERMISSION}.list`) || [],
  // permissionListPager: _.get(api, `${c.GET_PERMISSION}.pager`) || {},
  isLoading: api.loading.indexOf(c.GET_PERMISSION) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(RoleManagementContent);
